import type { EdgeId } from '@ipfabric/utils';
import { isPathLookupGraphMetadata } from 'components/Graph/GraphState';
import { highlightEdges } from 'components/Graph/commands/pathLookup/highlightEdges';
import { unhighlightEdges } from 'components/Graph/commands/pathLookup/unhighlightEdges';
import { EDGE_HIGHLIGHT_COLOR } from 'components/Graph/constants';
import type { GraphCommandFactory } from 'components/Graph/model/GraphCommandContext';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/lib/pipeable';
import { filterOutNonEdgePackets } from './helpers/filterOutNonEdgePackets';
import { findNodeIdByNodeSn } from './helpers/findNodeIdByNodeSn';
import { getFollowingPacketIds } from './helpers/getFollowingPacketIds';

export interface ClearSelectedHighlightedPathLookupEdgeCommand {
  detailId: EdgeId;
}

export const clearSelectedHighlightedPathLookupEdge: GraphCommandFactory<
  ClearSelectedHighlightedPathLookupEdgeCommand
> = (cmdContext, dispatch) => (command) => {
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
  cmdContext.updateState((draft) => {
    const { activeNodeSn: nodeSn } = draft.pathlookup.pathInspector;
    const nodeId = findNodeIdByNodeSn(draft.graph, nodeSn);

    if (nodeSn === null || nodeId === null) {
      return;
    }
    const decision =
      isPathLookupGraphMetadata(draft.graphMetadata) &&
      draft.graphMetadata.decisions[nodeId];
    if (!decision) {
      return;
    }
    const nodeHighlightedEdges =
      draft.pathlookup.pathInspector.highlightedEdges[nodeSn];
    const nodeSelectedPackets =
      draft.pathlookup.pathInspector.selectedPackets[nodeSn];
    if (
      nodeHighlightedEdges === undefined ||
      nodeSelectedPackets === undefined
    ) {
      return;
    }
    const incomingEdges = array.flatten(Object.values(decision.trafficIn));

    // If user removed first packet, highlight incoming edges
    if (nodeSelectedPackets.selectedIncomingPacket?.id === command.detailId) {
      nodeSelectedPackets.selectedIncomingPacket = null;
      nodeHighlightedEdges.incomingEdges = incomingEdges;
      dispatch(highlightEdges, {
        edges: incomingEdges,
        color: EDGE_HIGHLIGHT_COLOR,
      });
    }

    /**
     * Remove second edge if in both cases when user had removed second or first edge.
     * Second edge must not stay selected without selected first edge.
     * If first edge is still selected (second edge was removed),
     * highlight following edges to let user reselect new edge.
     * But if first edge was removed, unhighlight outgoing edges.
     */
    nodeSelectedPackets.selectedOutgoingPacket = null;
    if (nodeSelectedPackets.selectedIncomingPacket !== null) {
      const followingEdgeIds = pipe(
        getFollowingPacketIds(
          nodeSelectedPackets.selectedIncomingPacket.id,
          draft.graph.edges[nodeSelectedPackets.selectedIncomingPacket.id],
          draft.graph.nodes[nodeId],
        ),
        filterOutNonEdgePackets(draft.graph),
      );
      if (followingEdgeIds === null) {
        return;
      }
      nodeHighlightedEdges.outgoingEdges = followingEdgeIds;
      dispatch(highlightEdges, {
        edges: followingEdgeIds,
        color: EDGE_HIGHLIGHT_COLOR,
      });
    } else {
      dispatch(unhighlightEdges, {
        edges: nodeHighlightedEdges.outgoingEdges,
        color: true,
      });
      nodeHighlightedEdges.outgoingEdges = [];
    }
  });
};
